/**
 * LLM-assisted cognitive bias detection
 *
 * Analyzes prioritization sessions, cycles, and portfolios for patterns
 * indicative of well-known cognitive biases — sunk cost, HiPPO effect,
 * groupthink, recency bias, and more. Produces concrete, context-specific
 * illustrations so leaders can recognize the bias in their own work.
 *
 * Registry: cognitive_bias_detector — on-demand narrator. Transport: callAgentLlm,
 * so a failed run carries its reason to the screen.
 * Advisory only — never modifies any item, session, or decision state.
 * Inspired by Daniel Kahneman's insight that knowing about biases does not
 * protect against them; the goal is surfacing patterns the eye misses.
 */

import { callAgentLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import type {
  BiasAnalysisContext,
  BiasDetectionResult,
  DetectedBias,
  BiasType,
  BiasSeverity,
} from '../types/prioritization/cognitiveBias';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const COGNITIVE_BIAS_SYSTEM_PROMPT =
  'You are a behavioral decision analyst identifying cognitive biases in group prioritization sessions. ' +
  'Respond with valid JSON only. Be specific and grounded in the data provided — do not invent patterns.';

function buildBiasPrompt(ctx: BiasAnalysisContext): string {
  const itemLines = ctx.items.slice(0, 20).map((item, i) => {
    const parts: string[] = [`${i + 1}. "${item.title}"`];
    if (item.rank != null) parts.push(`rank #${item.rank}`);
    if (item.score != null) parts.push(`score ${item.score.toFixed(1)}`);
    if (item.scoreStdDev != null) parts.push(`stdDev ${item.scoreStdDev.toFixed(2)}`);
    if (item.cyclesAppeared != null) parts.push(`appeared in ${item.cyclesAppeared} cycles`);
    if (item.resourcesConsumed) parts.push(`resources consumed: ${item.resourcesConsumed}`);
    if (item.steward) parts.push(`owner: ${item.steward}`);
    if (item.createdAt) {
      const daysAgo = Math.round((Date.now() - new Date(item.createdAt).getTime()) / 86400000);
      parts.push(`created ${daysAgo}d ago`);
    }
    if (item.relationships?.length) {
      parts.push(`relationships: ${item.relationships.map(r => `${r.type} "${r.otherTitle}"`).join(', ')}`);
    }
    return '  ' + parts.join(' | ');
  }).join('\n');

  const participantLines = ctx.participants.slice(0, 12).map(p => {
    const parts: string[] = [p.name ?? p.email ?? p.id];
    if (p.role) parts.push(p.role);
    if (p.seniorityLevel) parts.push(p.seniorityLevel);
    if (p.avgScore != null) parts.push(`avg score: ${p.avgScore.toFixed(1)}`);
    return '  - ' + parts.join(', ');
  }).join('\n');

  const cutNote = ctx.cutLinePosition != null
    ? `Cut line: after rank #${ctx.cutLinePosition} (${ctx.cutLinePosition} items above the line, ${ctx.items.length - ctx.cutLinePosition} below)`
    : '';

  const previousCycleNote = ctx.previousCycleTopItemTitles?.length
    ? `Previous cycle top items: ${ctx.previousCycleTopItemTitles.slice(0, 8).map(t => `"${t}"`).join(', ')}`
    : '';

  const varianceNote = ctx.scoreVarianceAcrossItems != null
    ? `Score variance across items: ${ctx.scoreVarianceAcrossItems.toFixed(2)} (low = suspiciously uniform agreement)`
    : '';

  const dominantVoterNote = ctx.dominantVoterEmail
    ? `Potentially dominant voter: ${ctx.dominantVoterEmail}`
    : '';

  const recentEventNote = ctx.recentIncidentOrEvent
    ? `Recent event/incident that may be salient: "${ctx.recentIncidentOrEvent}"`
    : '';

  return `You are a behavioral decision analyst advising a team of business leaders on cognitive biases affecting their prioritization decisions.

Context:
- Scope: ${ctx.analysisScope}
${ctx.sessionName ? `- Session: "${ctx.sessionName}" (tool: ${ctx.sessionTool ?? 'unknown'})` : ''}
${ctx.cycleName ? `- Cycle: "${ctx.cycleName}"${ctx.cycleNumber != null ? ` (cycle #${ctx.cycleNumber})` : ''}` : ''}
- Participants: ${ctx.participants.length}
${participantLines ? `\nParticipant details:\n${participantLines}` : ''}
${varianceNote ? `\n${varianceNote}` : ''}
${ctx.participantAgreementRate != null ? `- Participant agreement rate: ${(ctx.participantAgreementRate * 100).toFixed(0)}%` : ''}
${dominantVoterNote ? `\n${dominantVoterNote}` : ''}
${cutNote ? `\n${cutNote}` : ''}
${previousCycleNote ? `\n${previousCycleNote}` : ''}
${recentEventNote ? `\n${recentEventNote}` : ''}
${ctx.criteriaLabels?.length ? `\nCriteria used: ${ctx.criteriaLabels.join(', ')}` : ''}

Items (ranked by session outcome, or listed if not yet ranked):
${itemLines || '  (no items provided)'}

Your task: Identify 1–4 cognitive biases that are most likely active in this prioritization context based on the patterns you observe. For each:

1. Choose the most specific bias type from this exact list:
   hippo_effect | groupthink | bandwagon_effect | recency_bias | availability_heuristic |
   anchoring_bias | confirmation_bias | sunk_cost_fallacy | status_quo_bias |
   narrative_fallacy | optimism_bias | planning_fallacy

2. Assess severity: "high" | "medium" | "low"

3. Assess your confidence: 0.0 to 1.0

4. Write a headline: one specific sentence about what is happening HERE — not a generic definition.

5. Write an explanation: 2–3 sentences on what this pattern indicates and why it matters for this team's decisions.

6. Write an illustration: a concrete, specific example using actual item names, scores, or participant patterns from the data. Make it undeniable — the kind of thing that makes a leader say "oh, that's us."

7. Write a mitigation: one specific, actionable countermeasure the team can apply before or during the next session.

8. List the evidence: up to 3 item titles that are central to this bias pattern.

Only report biases where you have real evidence from the data. Do not manufacture patterns. If the data is clean, report fewer biases. Focus on the most valuable signals.

Return JSON only, no markdown:
{
  "summary": "1–2 sentences on the overall bias risk in this session/cycle",
  "overallRiskLevel": "high" | "medium" | "low" | "none",
  "detectedBiases": [
    {
      "biasType": "one of the 12 types above",
      "severity": "high" | "medium" | "low",
      "confidence": 0.0-1.0,
      "headline": "Specific sentence about what is happening here",
      "explanation": "2-3 sentences on what this means for this team",
      "illustration": "Concrete example using actual item names/participants from the data",
      "mitigation": "One actionable countermeasure",
      "evidenceItemTitles": ["item title 1", "item title 2"]
    }
  ]
}`;
}

const VALID_BIAS_TYPES = new Set<string>([
  'hippo_effect', 'groupthink', 'bandwagon_effect', 'recency_bias',
  'availability_heuristic', 'anchoring_bias', 'confirmation_bias',
  'sunk_cost_fallacy', 'status_quo_bias', 'narrative_fallacy',
  'optimism_bias', 'planning_fallacy',
]);

const VALID_SEVERITIES = new Set<string>(['high', 'medium', 'low']);

function parseBiasResponse(
  content: string,
  ctx: BiasAnalysisContext
): BiasDetectionResult | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    const parsed = JSON.parse(jsonMatch[0]);

    const biases: DetectedBias[] = Array.isArray(parsed.detectedBiases)
      ? parsed.detectedBiases
          .filter(
            (b: unknown) =>
              b &&
              typeof b === 'object' &&
              VALID_BIAS_TYPES.has((b as Record<string, unknown>).biasType as string) &&
              typeof (b as Record<string, unknown>).headline === 'string'
          )
          .slice(0, 4)
          .map((b: Record<string, unknown>) => ({
            biasType: b.biasType as BiasType,
            severity: VALID_SEVERITIES.has(b.severity as string) ? (b.severity as BiasSeverity) : 'medium',
            confidence: typeof b.confidence === 'number' ? Math.min(1, Math.max(0, b.confidence)) : 0.5,
            headline: String(b.headline).trim(),
            explanation: typeof b.explanation === 'string' ? b.explanation.trim() : '',
            illustration: typeof b.illustration === 'string' ? b.illustration.trim() : '',
            mitigation: typeof b.mitigation === 'string' ? b.mitigation.trim() : '',
            evidenceItemTitles: Array.isArray(b.evidenceItemTitles)
              ? (b.evidenceItemTitles as unknown[]).filter(t => typeof t === 'string').slice(0, 3) as string[]
              : [],
          }))
      : [];

    const riskLevel =
      biases.length === 0 ? 'none' :
      biases.some(b => b.severity === 'high') ? 'high' :
      biases.some(b => b.severity === 'medium') ? 'medium' : 'low';

    return {
      analysisScope: ctx.analysisScope,
      contextLabel: ctx.sessionName ?? ctx.cycleName ?? ctx.workspaceName ?? 'Portfolio',
      generatedAt: new Date().toISOString(),
      detectedBiases: biases,
      overallRiskLevel: (VALID_SEVERITIES.has(parsed.overallRiskLevel) ? parsed.overallRiskLevel : riskLevel) as BiasSeverity | 'none',
      summary: typeof parsed.summary === 'string' ? parsed.summary.trim() : '',
    };
  } catch (e) {
    console.warn('[llmCognitiveBiasService] Parse failed:', e);
    return null;
  }
}

/**
 * Detect cognitive biases present in a prioritization session, cycle, or portfolio.
 * Returns null when there is nothing to run: the capability is off or there are
 * no items. Throws when the run fails — the model call (its message says why:
 * a refused key, a service that could not be reached) or an answer that could
 * not be read — so the screen can say what went wrong.
 */
export async function detectCognitiveBiases(
  context: BiasAnalysisContext
): Promise<BiasDetectionResult | null> {
  if (!isAiCapabilityEnabled('cognitive_bias_detector') || context.items.length === 0) {
    return null;
  }

  const content = await callAgentLlm({
    userPrompt: buildBiasPrompt(context),
    systemPrompt: COGNITIVE_BIAS_SYSTEM_PROMPT,
    conversationId: 'cognitive-bias-' + Date.now(),
    model: DEFAULT_MODEL,
    temperature: 0.3,
    maxTokens: 1200,
  });
  const result = parseBiasResponse(content, context);
  if (!result) {
    throw new Error('The AI answered, but its answer could not be read. Try again.');
  }
  return result;
}
